import { useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useReviews } from "hooks/LabelPropagation/useGetReview";
import { CLUSTER_DETAILS_PAGE, DATA_PAGE } from "helpers/constants/pages";
import DataList from "../DataList";
import {
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
} from "helpers/constants/miscellaneous";
import ClusterDetailsFilter from "../DataPageFilters/ClusterDetailsFilter";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import { Option } from "views/uikit/FormFields/SelectList";
import { useDispatch, useSelector } from "react-redux";
import { getTemporaryExportDataArray } from "redux/SingleDataset/selectors";
import { setTemporaryExportData } from "redux/SingleDataset/actions";

interface LabelPropagationProps {
  onLabelClick: (value: string[]) => void;
  options?: Option[];
  displayNewFloatingPanel?: boolean;
  isLabelPropagationEnabled: boolean;
}

interface ReviewListProps {
  onReviewAction: (sampleId?: string) => void;
  setReviewCountText: (text: string | null) => void;
  onNavigateToCluster: () => void;
  labelPropagation: LabelPropagationProps;
}
export default function ReviewList({
  onReviewAction,
  setReviewCountText,
  onNavigateToCluster,
  labelPropagation,
}: ReviewListProps) {
  const dispatch = useDispatch();
  const { datasetId, clusterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const flowId = searchParams.get(FLOW_PARAMETER);
  const labelId = searchParams.get(LABEL_CLASS_PARAMETER);

  const exportData = useSelector(getTemporaryExportDataArray);
  const clearSelectedMedia = () => dispatch(setTemporaryExportData(null));

  const { reviews, selectedReview, loading, bulkActionList } = useReviews(
    onReviewAction,
    datasetId,
    flowId,
    labelId,
    exportData,
    clearSelectedMedia,
  );

  const clusterLabel = useMemo(() => {
    if (!selectedReview) return undefined;

    return {
      text: selectedReview.label.name,
      onClick: () => {
        const params = new URLSearchParams(location.search);
        params.delete(LABEL_CLASS_PARAMETER);
        navigate({ search: `?${params.toString()}` });
      },
      tooltip: "Click to go back",
    };
  }, [selectedReview, location.search, navigate]);

  const showClusterFilter = (!!clusterId || !!labelId) && !!datasetId;
  useEffect(() => {
    onNavigateToCluster();
  }, [labelId, onNavigateToCluster]);

  // Calculate and set review count text based on context
  useEffect(() => {
    if (reviews && reviews.length > 0) {
      if (labelId && selectedReview) {
        const previews = selectedReview.previews || [];
        const totalImages = previews.length;

        // Count reviewed vs pending images
        // Images with floatingDetails "For review" are pending, others are reviewed
        const pendingImages = previews.filter(
          (preview) => preview.floatingDetails === "For review",
        ).length;
        const reviewedImages = totalImages - pendingImages;

        let countText = "";

        if (pendingImages === 0) {
          // All are reviewed
          countText = `${formatNumberWithCommas(totalImages)} ${totalImages === 1 ? "Image" : "Images"}`;
        } else if (reviewedImages === 0) {
          // None are reviewed
          countText = `${formatNumberWithCommas(totalImages)} ${totalImages === 1 ? "Image" : "Images"} for Review`;
        } else {
          // Mixed
          countText = `${formatNumberWithCommas(reviewedImages)} ${reviewedImages === 1 ? "Image" : "Images"} + ${formatNumberWithCommas(pendingImages)} for Review`;
        }

        setReviewCountText(countText);
      } else {
        // All reviews - clusters view
        const totalImages = reviews.reduce((total, review) => {
          return total + (review.previews?.length || 0);
        }, 0);

        const countText = `${formatNumberWithCommas(totalImages)} ${totalImages === 1 ? "Image" : "Images"}`;
        setReviewCountText(countText);
      }
    } else {
      setReviewCountText(null);
    }
  }, [reviews, selectedReview, labelId, setReviewCountText]);

  const limitedPreviewReviews = (reviews || []).map((review) => ({
    ...review,
    previews: review.previews?.slice(0, 9) ?? [],
  }));

  // Clear review count when component unmounts or when leaving review mode
  useEffect(() => {
    return () => {
      setReviewCountText(null);
    };
  }, [setReviewCountText]);

  return (
    <DataList
      page={labelId ? CLUSTER_DETAILS_PAGE : DATA_PAGE}
      clusters={limitedPreviewReviews || []}
      navigationCluster={labelId ? selectedReview : undefined}
      navigationClusterTags={undefined}
      isContentLoading={loading}
      showMetadataSummary={true}
      labelPropagation={labelPropagation}
      isDataPage={!!datasetId && !labelId}
      rightPanelMode="labels"
      title={{
        text: "Review",
        icon: showClusterFilter ? (
          <ClusterDetailsFilter label={clusterLabel} />
        ) : undefined,
      }}
      bulkActionList={bulkActionList}
    />
  );
}
